/*
  <product-plans> — three reactive purchase options (subscription / subscription + free starter
  pack / one-time). Reads per-variant pricing from an embedded JSON script, updates the cards on
  variant:change, and adds the selected plan to cart (with the native selling_plan for the
  subscription options, plus the free starter-pack variants for the starter option).

  The starter pack has an optional second product set tied to a flavour option value: variants
  flagged `alt_starter` in the embedded JSON use data-alt-starter-ids and show the alternate
  item list instead of the default one.
*/
(function () {
  function money(cents, format) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, format);
    }
    return '£' + (Math.round(cents) / 100).toFixed(2);
  }

  class ProductPlans extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-plans-data]');
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.data = { variants: {}, selected: null };
      }
      this.moneyFormat = this.getAttribute('data-money-format') || '';
      const idList = (attr) =>
        (this.getAttribute(attr) || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      this.starterIds = idList('data-starter-ids');
      // Alternate (flavour-specific) starter pack; empty when not configured.
      this.altStarterIds = idList('data-alt-starter-ids');

      this.saveLabel = this.getAttribute('data-save-label') || 'SAVE';
      this.soldOutLabel = this.getAttribute('data-soldout-label') || 'Sold out';
      this.hideStarterServings = (this.getAttribute('data-starter-hide-servings') || '')
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n));
      this.cards = Array.from(this.querySelectorAll('.plan-card'));
      this.cta = this.querySelector('[data-plans-cta]');
      this.currentId = this.data.selected;

      // The preferred plan starts as the configured default and updates whenever the shopper
      // picks a plan. render() honors it whenever that plan is available and RESTORES it if it
      // was only temporarily hidden (e.g. starter is hidden for some serving counts), instead
      // of falling back to subscription one-way and losing the default.
      this.preferredPlan = this.getAttribute('data-default-plan') || 'subscription';

      // Sticky buy button: move it to <body> so position:fixed isn't broken by any
      // transformed ancestor (e.g. the product-info reveal animation), then wire its click
      // to the same add-to-cart and show it once the main CTA scrolls out of view.
      this.stickyBar = this.querySelector('[data-plans-sticky]');
      if (this.stickyBar) {
        // Drop any stale copy left in <body> by a previous connect for this block.
        const owner = this.stickyBar.getAttribute('data-owner');
        if (owner) {
          document.querySelectorAll(`body > [data-plans-sticky][data-owner="${owner}"]`).forEach((el) => el.remove());
        }
        document.body.appendChild(this.stickyBar);
        this.stickyCta = this.stickyBar.querySelector('[data-plans-sticky-cta]');
        if (this.stickyCta) this.stickyCta.addEventListener('click', () => this.attemptAddToCart());
        this.initStickyObserver();
      }

      // Repeat starter-claim warning modal (soft, cookie-based) — mirrors the multi-step
      // flow. Moved to <body> so its fixed overlay isn't broken by a transformed ancestor.
      this.starterModal = this.querySelector('[data-plans-starter-modal]');
      if (this.starterModal) {
        const mOwner = this.starterModal.getAttribute('data-owner');
        if (mOwner) {
          document.querySelectorAll(`body > [data-plans-starter-modal][data-owner="${mOwner}"]`).forEach((el) => el.remove());
        }
        document.body.appendChild(this.starterModal);
        this.starterModal.querySelectorAll('[data-plans-starter-close]').forEach((el) =>
          el.addEventListener('click', () => this.closeStarterModal())
        );
        const cont = this.starterModal.querySelector('[data-plans-starter-continue]');
        if (cont) cont.addEventListener('click', () => { this.closeStarterModal(); this.addToCart(); });
      }

      this.cards.forEach((card) => {
        const radio = card.querySelector('.plan-radio');
        radio.addEventListener('change', () => {
          // A shopper's explicit choice becomes the new preferred plan.
          this.preferredPlan = card.getAttribute('data-plan');
          this.selectCard(card);
        });
      });

      if (this.cta) this.cta.addEventListener('click', () => this.attemptAddToCart());

      // Update when the variant (flavour / quantity) changes.
      this.onVariantChange = (event) => {
        if (event.detail && event.detail.variant) {
          this.currentId = String(event.detail.variant.id);
          this.render();
        }
      };
      (this.productForm() || document).addEventListener('variant:change', this.onVariantChange);

      this.render();
    }

    disconnectedCallback() {
      (this.productForm() || document).removeEventListener('variant:change', this.onVariantChange);
      this.stickyObserver?.disconnect();
      // The sticky bar and starter modal were relocated to <body>, so remove them explicitly.
      this.stickyBar?.remove();
      this.starterModal?.remove();
    }

    // --- Repeat starter-claim warning (soft, cookie-based) ---

    getCookie(name) {
      const value = '; ' + document.cookie;
      const parts = value.split('; ' + name + '=');
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    }

    setCookie(name, value, days) {
      let expires = '';
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + value + expires + '; path=/; SameSite=Lax';
    }

    // Persist to BOTH localStorage and a cookie, and treat either as claimed. The cookie is
    // shared with the multi-step starter flow (and works across the live site); localStorage
    // is the reliable path inside the theme editor, where the storefront runs in a
    // cross-site iframe and third-party cookie WRITES are blocked.
    starterClaimed() {
      try {
        if (window.localStorage && localStorage.getItem('starter_pack_claimed') === '1') return true;
      } catch (e) {}
      return this.getCookie('starter_pack_claimed') === '1';
    }

    setStarterClaimed() {
      try {
        if (window.localStorage) localStorage.setItem('starter_pack_claimed', '1');
      } catch (e) {}
      this.setCookie('starter_pack_claimed', '1', 365);
    }

    openStarterModal() {
      if (this.starterModal) this.starterModal.removeAttribute('hidden');
    }

    closeStarterModal() {
      if (this.starterModal) this.starterModal.setAttribute('hidden', '');
    }

    // Gate the CTA: warn before adding the starter plan again if it was already claimed.
    attemptAddToCart() {
      if (this.activePlan === 'starter' && this.starterModal && this.starterClaimed()) {
        this.openStarterModal();
        return;
      }
      this.addToCart();
    }

    initStickyObserver() {
      if (!this.cta || !this.stickyBar || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          // Only count the CTA as "passed" once it has scrolled ABOVE the viewport top —
          // not while it's still below the fold on page load / before you reach it.
          this._ctaPassed = entries[0].boundingClientRect.bottom <= 0;
          this.syncSticky();
        },
        { threshold: 0 }
      );
      this.stickyObserver.observe(this.cta);
    }

    // Show the sticky bar only once the main CTA has been scrolled past (it's above the
    // viewport) and the product is purchasable (never over a sold-out state).
    syncSticky() {
      if (!this.stickyBar) return;
      const v = this.data.variants[this.currentId];
      const soldOut = !v || !v.available;
      const show = !!this._ctaPassed && !soldOut;
      this.stickyBar.classList.toggle('is-visible', show);
      this.stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    productForm() {
      const id = this.getAttribute('data-product-form-id');
      return id ? document.getElementById(id) : null;
    }

    selectCard(card) {
      this.cards.forEach((c) => c.classList.toggle('is-selected', c === card));
      this.updateCta();
    }

    get activePlan() {
      const checked = this.querySelector('.plan-radio:checked');
      return checked ? checked.value : 'subscription';
    }

    // The starter-pack variant ids for a variant: the alternate set when that variant's
    // flavour is flagged for it and the set is configured, otherwise the default set.
    starterIdsFor(v) {
      return v && v.alt_starter && this.altStarterIds.length ? this.altStarterIds : this.starterIds;
    }

    // Price/compare for a given plan on a given variant.
    planPricing(plan, v) {
      if (plan === 'onetime') return { price: v.one_time, compare: v.one_time_compare };
      if (plan === 'starter') return { price: v.sub, compare: v.starter_compare };
      return { price: v.sub, compare: v.sub_compare };
    }

    // Show the selected plan's price/compare in the CTA, and toggle the subscription-only note.
    updateCta() {
      const v = this.data.variants[this.currentId];
      const soldOut = !v || !v.available;
      const plan = this.activePlan;
      const priceEl = this.querySelector('[data-cta-price]');
      const compareEl = this.querySelector('[data-cta-compare]');
      if (v && (priceEl || compareEl)) {
        const { price, compare } = this.planPricing(plan, v);
        if (priceEl) priceEl.textContent = money(price, this.moneyFormat);
        if (compareEl) {
          if (compare && compare > price) {
            compareEl.textContent = money(compare, this.moneyFormat);
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
      }
      const subNote = this.querySelector('[data-cta-sub-note]');
      if (subNote) subNote.hidden = plan === 'onetime';
      const starterNote = this.querySelector('[data-cta-starter-note]');
      if (starterNote) starterNote.hidden = plan !== 'starter';

      // Express payment buttons (Apple Pay / Shop Pay): sync the variant + selling plan.
      // Hidden for the starter plan because express checkout bypasses the cart and can't
      // include the free gift SKUs.
      const paymentWrap = this.querySelector('[data-plans-payment]');
      if (paymentWrap) {
        const idInput = paymentWrap.querySelector('[data-payment-id]');
        const planInput = paymentWrap.querySelector('[data-payment-plan]');
        if (idInput) idInput.value = this.currentId;
        if (planInput) planInput.value = plan !== 'onetime' && v && v.plan ? v.plan : '';
        paymentWrap.hidden = plan === 'starter' || soldOut;
      }

      // Sold out: disable the main CTA, swap its label to "Sold out", and hide the price.
      if (this.cta) {
        this.cta.classList.toggle('plans-cta--soldout', soldOut);
        this.cta.disabled = soldOut;
        const labelEl = this.cta.querySelector('.plans-cta__label');
        if (labelEl) {
          if (labelEl.dataset.tmpl === undefined) labelEl.dataset.tmpl = labelEl.textContent;
          labelEl.textContent = soldOut ? this.soldOutLabel : labelEl.dataset.tmpl;
        }
        if (priceEl) priceEl.hidden = soldOut;
        if (compareEl && soldOut) compareEl.setAttribute('hidden', '');
      }

      // Mirror everything onto the sticky bar's CTA.
      if (this.stickyBar) {
        const sPrice = this.stickyBar.querySelector('[data-sticky-price]');
        const sCompare = this.stickyBar.querySelector('[data-sticky-compare]');
        const sLabel = this.stickyBar.querySelector('[data-sticky-label]');

        // Show the selected plan's name (with {X} servings already filled in by render()).
        const sPlanTitle = this.stickyBar.querySelector('[data-sticky-plan-title]');
        if (sPlanTitle) {
          const selectedCard = this.querySelector('.plan-card.is-selected');
          const planTitleEl = selectedCard && selectedCard.querySelector('[data-plan-title]');
          if (planTitleEl) sPlanTitle.textContent = planTitleEl.textContent;
        }
        if (v) {
          const { price, compare } = this.planPricing(plan, v);
          if (sPrice) sPrice.textContent = money(price, this.moneyFormat);
          if (sCompare) {
            if (compare && compare > price) {
              sCompare.textContent = money(compare, this.moneyFormat);
              sCompare.removeAttribute('hidden');
            } else {
              sCompare.setAttribute('hidden', '');
            }
          }
        }
        if (this.stickyCta) {
          this.stickyCta.classList.toggle('plans-sticky__cta--soldout', soldOut);
          this.stickyCta.disabled = soldOut;
        }
        if (sLabel) {
          if (sLabel.dataset.tmpl === undefined) sLabel.dataset.tmpl = sLabel.textContent;
          sLabel.textContent = soldOut ? this.soldOutLabel : sLabel.dataset.tmpl;
        }
        if (sPrice) sPrice.hidden = soldOut;
        if (sCompare && soldOut) sCompare.setAttribute('hidden', '');
        this.syncSticky();
      }
    }

    render() {
      const v = this.data.variants[this.currentId];
      const soldOut = !v || !v.available;
      this.cards.forEach((card) => {
        const plan = card.getAttribute('data-plan');
        const titleEl = card.querySelector('[data-plan-title]');
        const priceEl = card.querySelector('[data-plan-price]');
        const compareEl = card.querySelector('[data-plan-compare]');
        const perEl = card.querySelector('[data-plan-per]');
        const radio = card.querySelector('.plan-radio');
        const soldOutBadge = card.querySelector('[data-plan-soldout]');

        // Whole variant sold out: keep every plan visible but disabled and badged
        // (the CTA is disabled separately in updateCta). Otherwise hide subscription /
        // starter options that have no selling plan for this variant.
        card.classList.toggle('plan-card--soldout', soldOut);
        if (radio) radio.disabled = soldOut;
        if (soldOutBadge) soldOutBadge.toggleAttribute('hidden', !soldOut);

        if (soldOut) {
          card.removeAttribute('hidden');
          if (!v) return;
        } else if (plan !== 'onetime' && !v.plan) {
          card.setAttribute('hidden', '');
          return;
        }

        // Hide the starter pack option for configured serving counts (e.g. 14).
        if (plan === 'starter' && v && this.hideStarterServings.indexOf(v.servings) !== -1) {
          card.setAttribute('hidden', '');
          return;
        }

        card.removeAttribute('hidden');

        const servings = v.servings || 0;
        const fillX = (el) => {
          if (!el) return;
          if (el.dataset.tmpl === undefined) el.dataset.tmpl = el.textContent;
          el.textContent = (el.dataset.tmpl || '').replace(/\{X\}/g, servings);
        };
        // Starter card with an alternate label configured: point the title at whichever
        // template this variant's starter set uses before {X} is filled in, so the single
        // [data-plan-title] element (which the sticky bar also reads) stays authoritative.
        if (plan === 'starter' && titleEl) {
          const altTitle = card.getAttribute('data-alt-title');
          const useAltTitle = !!v.alt_starter && this.altStarterIds.length > 0 && altTitle;
          titleEl.dataset.tmpl = useAltTitle ? altTitle : card.getAttribute('data-default-title') || '';
        }

        // {X} placeholder in the title and the description.
        fillX(titleEl);
        fillX(card.querySelector('[data-plan-desc]'));

        const { price, compare } = this.planPricing(plan, v);

        if (priceEl) priceEl.textContent = money(price, this.moneyFormat);
        if (compareEl) {
          if (compare && compare > price) {
            compareEl.textContent = money(compare, this.moneyFormat);
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
        if (perEl) {
          if (servings > 0) {
            perEl.textContent = money(price / servings, this.moneyFormat) + ' / serving';
            perEl.removeAttribute('hidden');
          } else {
            perEl.setAttribute('hidden', '');
          }
        }

        // "SAVE {amount}" badge (total saving = compare - price).
        const saveEl = card.querySelector('[data-plan-save]');
        if (saveEl) {
          const saving = compare - price;
          if (compare && saving > 0) {
            const prefix = saveEl.getAttribute('data-save-label') || 'SAVE';
            saveEl.textContent = (prefix ? prefix + ' ' : '') + money(saving, this.moneyFormat);
            saveEl.removeAttribute('hidden');
          } else {
            saveEl.setAttribute('hidden', '');
          }
        }

        // Starter card: show the item list for this variant's starter set, and fill in each
        // list's main-product line "{label} (was → now)".
        if (plan === 'starter') {
          const useAlt = !!v.alt_starter && this.altStarterIds.length > 0;
          card.querySelectorAll('[data-starter-items]').forEach((list) => {
            list.toggleAttribute('hidden', (list.getAttribute('data-starter-items') === 'alt') !== useAlt);
          });
          card.querySelectorAll('[data-main-label]').forEach(fillX);
          card
            .querySelectorAll('[data-main-compare]')
            .forEach((el) => (el.textContent = money(v.one_time, this.moneyFormat)));
          card
            .querySelectorAll('[data-main-price]')
            .forEach((el) => (el.textContent = money(v.sub, this.moneyFormat)));
        }
      });

      // Pick the plan to show: the preferred plan when it's available; otherwise keep the
      // current visible selection; otherwise the first visible card. This restores the
      // preferred/default plan once it becomes available again rather than sticking on a
      // one-way fallback.
      const isVisible = (c) => c && !c.hasAttribute('hidden');
      let target = this.cards.find((c) => c.getAttribute('data-plan') === this.preferredPlan);
      if (!isVisible(target)) {
        const current = this.querySelector('.plan-card.is-selected');
        target = isVisible(current) ? current : this.cards.find(isVisible);
      }
      if (target) {
        const radio = target.querySelector('.plan-radio');
        if (radio && !radio.checked) radio.checked = true;
        if (!target.classList.contains('is-selected')) this.selectCard(target);
      }

      this.updateCta();
    }

    setLoading(on) {
      [this.cta, this.stickyCta].forEach((btn) => {
        if (!btn) return;
        btn.toggleAttribute('aria-busy', on);
        btn.disabled = on;
      });
    }

    addToCart() {
      const v = this.data.variants[this.currentId];
      if (!v) return;
      const plan = this.activePlan;

      // Record that a starter pack was claimed so a repeat add warns next time (shared with
      // the multi-step starter flow via the same cookie/localStorage key).
      if (plan === 'starter') this.setStarterClaimed();

      const main = { id: Number(this.currentId), quantity: 1 };
      if ((plan === 'subscription' || plan === 'starter') && v.plan) {
        main.selling_plan = Number(v.plan);
      }

      // Add the free starter products first, then the main product last, so the cart
      // (which lists most-recently-added at the top) shows the main product above the gifts.
      // A shared _starter_group token links the gifts to THIS primary line, so the cart can
      // remove them when that specific product is removed (even if other items remain).
      const items = [];
      if (plan === 'starter') {
        const group = 'sp-' + Date.now();
        main.properties = { _starter_group: group };
        this.starterIdsFor(v).forEach((id) =>
          items.push({ id: Number(id), quantity: 1, properties: { _free_gift: 'starter', _starter_group: group } })
        );
      }
      items.push(main);

      const routes = (window.theme && theme.routes) || {};
      const addUrl = routes.cart_add_url || '/cart/add';
      const cartUrl = routes.cart_url || '/cart';

      // Gather sections to render — the cart drawer registers its own section id.
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      this.setLoading(true);
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: sectionsToBundle, sections_url: window.location.pathname }),
      })
        .then((r) => r.json())
        .then(async (res) => {
          if (res && res.status) {
            this.setLoading(false);
            console.warn('Plans add to cart error:', res.description || res.message);
            return;
          }

          // On the cart page there's no drawer — go to the cart.
          if (document.body.classList.contains('template-cart') || (window.theme && theme.settings && theme.settings.cartType === 'page')) {
            window.location.href = cartUrl;
            return;
          }

          // Refresh the cart UI (count + drawer contents) and open the drawer, like a normal add.
          let cartJson = null;
          try {
            cartJson = await (await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })).json();
            cartJson.sections = res.sections;
          } catch (e) {}
          if (window.theme && theme.pubsub && cartJson) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-plans', cart: cartJson });
          }

          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.show === 'function') {
            drawer.show();
            this.setLoading(false);
          } else {
            window.location.href = cartUrl;
          }
        })
        .catch(() => this.setLoading(false));
    }
  }

  if (!customElements.get('product-plans')) {
    customElements.define('product-plans', ProductPlans);
  }
})();
